import { Navigate, useParams } from 'react-router-dom';
import { LiksPage } from '../../components/liks/LiksPage/LiksPage';
import { LiksPageHeading } from '../../components/liks/LiksPageHeading/LiksPageHeading';
import { useTenantUserQuery } from '../../features/tenant/queries/useTenantUserQuery';
import { useI18n } from '../../i18n/useI18n';
import { TenantUserDetailsAdminBar } from './TenantUserDetailsAdminBar';
import { TenantUserDetailsForm } from './TenantUserDetailsForm';
import { TenantUserLabelsGroupsCard } from './TenantUserLabelsGroupsCard';
import {
  resolveTenantTabPath,
  resolveTenantUserPath,
} from '../../routes/tenantPaths';
import './TenantUserDetailsPage.css';

function formatUserHeading(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  username: string | null | undefined,
  fallback: string,
): string {
  const fullName = [firstName?.trim(), lastName?.trim()]
    .filter((part): part is string => Boolean(part))
    .join(' ');

  if (fullName !== '') {
    return fullName;
  }

  const trimmedUsername = username?.trim();
  if (trimmedUsername) {
    return trimmedUsername;
  }

  return fallback;
}

export function TenantUserDetailsPage() {
  const { translate } = useI18n();
  const { userId } = useParams<{ userId: string }>();
  const userQuery = useTenantUserQuery(userId);
  const profile = userQuery.data;

  if (!userId) {
    return <Navigate to={resolveTenantTabPath('users')} replace />;
  }

  if (userQuery.isLoading) {
    return (
      <LiksPage
        size="big"
        content={
          <p className="tenant-user-details__status">
            {translate('organisationManagement.users.details.loading')}
          </p>
        }
      />
    );
  }

  if (userQuery.isError || !profile) {
    return (
      <LiksPage
        size="big"
        header={
          <LiksPageHeading
            aria-label={translate(
              'organisationManagement.users.details.breadcrumbLegend',
            )}
            items={[
              {
                label: translate('organisationManagement.title'),
                to: resolveTenantTabPath('users'),
              },
              {
                label: translate('organisationManagement.users.details.title'),
              },
            ]}
          />
        }
        content={
          <p className="tenant-user-details__status" role="alert">
            {translate('organisationManagement.users.details.loadError')}
          </p>
        }
      />
    );
  }

  const profileUserId = profile.id?.trim();
  if (profileUserId && profileUserId !== userId) {
    return <Navigate to={resolveTenantUserPath(profileUserId)} replace />;
  }

  const headingName = formatUserHeading(
    profile.firstName,
    profile.lastName,
    profile.username,
    translate('organisationManagement.users.unknownUser'),
  );

  return (
    <LiksPage
      size="big"
      className="tenant-user-details"
      header={
        <LiksPageHeading
          aria-label={translate(
            'organisationManagement.users.details.breadcrumbLegend',
          )}
          items={[
            {
              label: translate('organisationManagement.title'),
              to: resolveTenantTabPath(),
            },
            {
              label: translate('organisationManagement.tabs.users'),
              to: resolveTenantTabPath('users'),
            },
            { label: headingName },
          ]}
        />
      }
      content={
        <div className="tenant-user-details__content">
          <TenantUserDetailsAdminBar
            key={`${userId}:admin`}
            profile={profile}
            userId={userId}
          />
          <TenantUserDetailsForm
            key={userId}
            profile={profile}
            userId={userId}
          />
          <TenantUserLabelsGroupsCard
            key={`${userId}:labels-groups`}
            userId={userId}
          />
        </div>
      }
    />
  );
}
